/**
 * The doctor surface's shared formats — the canvases print a name, a day, a
 * duration and a test in one shape everywhere, so the shape lives once here.
 *
 * Data varies; the FORMAT does not. "Ahmed H." on Today and "Ahmed H." in the
 * Queue are the same object, and two call sites that each abbreviate a name
 * their own way is how the same patient ends up reading two different ways on
 * two screens.
 */

interface Named {
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * "Ahmed H." — the canvases' list-row name. First name and the initial of the
 * last: enough to call someone from the waiting room, and it keeps a row on
 * one line beside its chips. The full name is the patient file's heading.
 */
export function shortName(p: Named | null | undefined, fallback = ''): string {
  const first = (p?.firstName ?? '').trim();
  const last = (p?.lastName ?? '').trim();
  if (!first && !last) return fallback;
  if (!last) return first;
  if (!first) return last;
  return `${first} ${last.charAt(0).toUpperCase()}.`;
}

/**
 * "Hoda S." — the same abbreviation, applied to a name that arrives as ONE
 * string (a staff name the server denormalised, a prescriber, whoever took a
 * reading). The canvases abbreviate every staff name this way, and a screen
 * that prints "Hoda S." in a table and "Hoda Sharaf" in the pane beside it is
 * two conventions on one card.
 */
export function shortLabel(full: string | null | undefined, fallback = ''): string {
  const parts = String(full ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  /* An honorific is part of the first name, not a name of its own:
     "Dr. Mostafa Adam" abbreviates to "Dr. Mostafa A.", never "Dr. M." */
  const head = /^(dr|prof|mr|mrs|ms)\.?$/i.test(parts[0]) ? 2 : 1;
  if (parts.length <= head) return parts.join(' ');
  const last = parts[parts.length - 1];
  return `${parts.slice(0, head).join(' ')} ${last.charAt(0).toUpperCase()}.`;
}

/** "Ahmed Hassan" — the full name, for headings. */
export function fullName(p: Named | null | undefined, fallback = ''): string {
  const s = [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim();
  return s || fallback;
}

/** "Sunday 6 Sep" — the top bar's day. No comma, day before month. */
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/* Fixed three-letter months — ICU's en-GB now prints "Sept", which is not the
   canvases' format and would drift again with the next ICU update. */
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function dayLabel(d: Date = new Date()): string {
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "6 Sep" — a day in a table cell or a sub-line. */
export function dayMonth(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? parseDay(iso) : new Date(iso)) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "6 Sep 2026" */
export function dayMonthYear(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? parseDay(iso) : new Date(iso)) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Sep 8" — a calendar date in a table cell. */
export function shortDate(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? parseDay(iso) : new Date(iso)) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/**
 * A `date` column arrives as "YYYY-MM-DD" — parsing that with `new Date()`
 * reads it as UTC midnight, which is the previous evening here. Read the
 * calendar day as a LOCAL day instead.
 */
export function parseDay(s: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(s);
}

/** Today's calendar day, local, as the API's `date` string. */
export function todayIso(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "11 min ago" · "3 h ago" · "2 d ago" — how long something has been waiting. */
export function ago(iso: string | Date | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const t = typeof iso === 'string' ? new Date(iso).getTime() : iso.getTime();
  if (!Number.isFinite(t)) return '';
  const min = Math.max(0, Math.round((now - t) / 60_000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

/** Whole days between a moment and now, never negative. */
export function daysSince(iso: string | null | undefined, now = Date.now()): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((now - t) / 86_400_000));
}

/** "45 M" — the age and sex the canvases print beside a name. */
export function ageSex(p: { dateOfBirth?: string | null; gender?: string | null } | null | undefined): string {
  if (!p) return '';
  const bits: string[] = [];
  if (p.dateOfBirth) {
    const dob = parseDay(String(p.dateOfBirth).slice(0, 10));
    const years = Math.floor((Date.now() - dob.getTime()) / 31_557_600_000);
    if (Number.isFinite(years) && years >= 0) bits.push(String(years));
  }
  if (p.gender === 'male') bits.push('M');
  else if (p.gender === 'female') bits.push('F');
  return bits.join(' ');
}

/**
 * "K⁺" — the analyte as a clinician writes it on a banner. Only the ones
 * with a conventional symbol are abbreviated; anything else prints as the lab
 * named it, which is never wrong, only longer.
 */
const ANALYTE: Record<string, string> = {
  potassium: 'K⁺',
  sodium: 'Na⁺',
  chloride: 'Cl⁻',
  calcium: 'Ca²⁺',
  magnesium: 'Mg²⁺',
  hemoglobin: 'Hb',
  haemoglobin: 'Hb',
  'blood glucose': 'Glucose',
  'fasting blood glucose': 'FBG',
  'international normalized ratio': 'INR',
  creatinine: 'Creatinine',
  'white blood cell count': 'WBC',
  platelets: 'Platelets',
  'platelet count': 'Platelets',
  'hemoglobin a1c': 'HbA1c',
  hba1c: 'HbA1c',
  troponin: 'Troponin',
};
export function analyte(name: string | null | undefined): string {
  if (!name) return '';
  return ANALYTE[name.trim().toLowerCase()] ?? name.trim();
}

/** "EGP 1,900" */
export function egp(n: number | null | undefined): string {
  return `EGP ${Math.round(Number(n ?? 0)).toLocaleString('en-US')}`;
}

/** A visit's kind as the canvases say it: "New visit" / "Follow-up". */
export function visitKind(type: string | null | undefined): string {
  const t = String(type ?? '').replace(/-/g, '_');
  if (t === 'follow_up') return 'Follow-up';
  if (t === 'progress_note') return 'Progress note';
  if (t === 'procedure') return 'Procedure';
  if (t === 'emergency') return 'Emergency';
  return 'New visit';
}

/** "his" / "her" / "their" — for the lines that talk about a patient. */
export function pronoun(gender: string | null | undefined, cap = false): string {
  const p = gender === 'male' ? 'his' : gender === 'female' ? 'her' : 'their';
  return cap ? p.charAt(0).toUpperCase() + p.slice(1) : p;
}

/**
 * "two" — a small count written as a word, the way the canvases write one
 * inside a sentence ("Today's two are both still waiting"). Prose takes the
 * word; a chip, a column and a count take the numeral.
 */
const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
export function numberWord(n: number): string {
  return Number.isInteger(n) && n >= 0 && n < WORDS.length ? WORDS[n] : String(n);
}

/**
 * "1 w ago" · "3 mo ago" — how long ago something happened, at the coarseness
 * a person says it out loud. `ago()` answers in minutes and hours because it
 * is used on a waiting room; this one is used on a chart, where "12 Sep 2026"
 * is a date a human has to convert in their head to answer "recently?".
 */
export function agoCoarse(iso: string | Date | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const t = typeof iso === 'string' ? new Date(iso).getTime() : iso.getTime();
  if (!Number.isFinite(t)) return '';
  const d = Math.floor((now - t) / 86_400_000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} d ago`;
  if (d < 30) return `${Math.round(d / 7)} w ago`;
  if (d < 365) return `${Math.round(d / 30)} mo ago`;
  return `${Math.floor(d / 365)} y ago`;
}

/**
 * "BID" — the sig as it is written on a script. The canvases print the
 * abbreviation ("Esomeprazole 40 mg — BID × 4 wks"), never the sentence, so a
 * drug card title stays on one line.
 *
 * The code is usually already on the row (`frequency.code` — QD, BID, TDS);
 * this only has to work when it is not, and it never invents one: a frequency
 * it does not recognise is returned as written.
 *
 * The remainder comes back separately, because "Once daily BEFORE BREAKFAST"
 * is a frequency and a timing run together, and the timing belongs on the
 * card's second line with the prescriber.
 */
const SIGS: Array<[RegExp, string]> = [
  [/^(once daily|once a day|one time daily|daily|od|qd|q\.?d\.?)\b/i, 'OD'],
  [/^(twice daily|twice a day|two times daily|bid|bd|b\.?i\.?d\.?)\b/i, 'BID'],
  [/^(three times (a day|daily)|thrice daily|tid|tds|t\.?i\.?d\.?)\b/i, 'TDS'],
  [/^(four times (a day|daily)|qid|qds|q\.?i\.?d\.?)\b/i, 'QID'],
  [/^(every (\d+) h(ours?)?|q ?(\d+) ?h)\b/i, 'q$2$4h'],
  [/^(as needed|when required|prn|p\.?r\.?n\.?)\b/i, 'PRN'],
  [/^(at bedtime|at night|nightly|every night|nocte|qhs)\b/i, 'nocte'],
  [/^(every morning|each morning|mane|qam)\b/i, 'mane'],
  [/^(once weekly|weekly|every week)\b/i, 'weekly'],
];
export function sigShort(frequency: string | null | undefined): { sig: string; rest: string } {
  const text = String(frequency ?? '').trim();
  if (!text) return { sig: '', rest: '' };
  /* Already a code — "QD", "BID". Uppercase, short, no spaces. */
  if (/^[A-Za-z]{2,5}$/.test(text) && text === text.toUpperCase()) return { sig: text, rest: '' };
  for (const [re, out] of SIGS) {
    const m = re.exec(text);
    if (m) {
      return {
        sig: out.replace(/\$(\d)/g, (_, i) => m[Number(i)] ?? ''),
        rest: text.slice(m[0].length).trim().replace(/^[—–-]\s*/, ''),
      };
    }
  }
  return { sig: text, rest: '' };
}

/**
 * "BID" → "twice a day". THE PATIENT'S SHEET IS NOT WRITTEN IN SIG CODES.
 * The same abbreviations a script is read in are the ones a patient misreads,
 * so the sheet that goes home with them spells the frequency out. This is a
 * translation of what is recorded, never an addition to it: a code the table
 * does not know comes back exactly as it was written.
 */
const SIG_WORDS: Array<[RegExp, string]> = [
  [/^(od|qd)$/i, 'once a day'],
  [/^(bid|bd)$/i, 'twice a day'],
  [/^(tds|tid)$/i, 'three times a day'],
  [/^(qid|qds)$/i, 'four times a day'],
  [/^q ?(\d+) ?h$/i, 'every $1 hours'],
  [/^prn$/i, 'when needed'],
  [/^nocte$/i, 'at night'],
  [/^mane$/i, 'in the morning'],
  [/^weekly$/i, 'once a week'],
];
export function sigWords(sig: string | null | undefined): string {
  const code = String(sig ?? '').trim();
  if (!code) return '';
  for (const [re, out] of SIG_WORDS) {
    const m = re.exec(code);
    if (m) return out.replace(/\$(\d)/g, (_, i) => m[Number(i)] ?? '');
  }
  return code;
}

/** "for 14 days" — the course length written out, for the same sheet. */
const DURATION_WORDS: Record<string, string> = {
  d: 'day', wks: 'week', mo: 'month', y: 'year',
};
export function durationWords(
  n: string | number | null | undefined,
  unit: string | null | undefined,
): string {
  const count = Number(n);
  if (!count || Number.isNaN(count)) return '';
  const word = DURATION_WORDS[durationShort(unit)] ?? 'day';
  return `for ${count} ${word}${count === 1 ? '' : 's'}`;
}

/** "× 4 wks" · "× 14 d" — the course length, abbreviated as a script writes it. */
const DURATIONS: Record<string, string> = {
  day: 'd', days: 'd', d: 'd',
  week: 'wks', weeks: 'wks', wk: 'wks', wks: 'wks', w: 'wks',
  month: 'mo', months: 'mo', mo: 'mo', mos: 'mo',
  year: 'y', years: 'y', y: 'y',
};
export function durationShort(unit: string | null | undefined): string {
  const u = String(unit ?? '').trim().toLowerCase();
  return DURATIONS[u] ?? (u || 'd');
}
